// @ts-ignore - allow import with query string
import { fetchJSON } from './util.js?v=25';
// @ts-ignore - allow import query string
import { mergeDaysWithLegacyFallback, resolveRequestSystem } from './booking-availability.js?v=1';

window.CubittsAPI = new (class CubittsAPI {
  constructor() {
    this._accessKey = window.atob('Rlc3bU52LUR6VE1KIWxMSUk/bWF1c1c5dkF1TUdxZnZ3Ti1XWWthMQ==');
    const api = window.theme.isDevTheme ? 'https://staging.cubittsadmin.com' : (window.theme.apiUrl || 'https://www.cubittsadmin.com');
    this._baseUrl = `${api}/api/shopify`;
    // New system uses app-proxy Lambda (same pattern as PD tool)
    const appProxyUrl = window.theme.isDevTheme
      ? 'https://staging.app-proxy.cubitts.com'
      : 'https://app-proxy.cubitts.com';
    this._newSystemBaseUrl = `${appProxyUrl}/api/shopify`;
    // Track which system each practice uses (populated when fetching locations)
    this._practiceSystemMap = new Map();
    // PMS practice id -> Sylius channel id, for practices that exist in both systems
    this._practiceLegacyIdMap = new Map();
    // Day id -> system, so time/booking calls follow the day the customer picked
    this._daySystemMap = new Map();
    // PMS practice id -> YYYY-MM-DD the practice moves to PMS, or null if it has none
    this._practiceCutoverMap = new Map();
  }

  /**
   * Record which system each practice belongs to, and for cut-over practices the Sylius
   * channel id they were deduplicated against, so their pre-cutover days stay reachable.
   *
   * @param {any[]} newPractices - normalized PMS practices
   * @param {any[]} oldPractices - normalized Sylius practices, before deduplication
   * @param {any[]} keptOldPractices - Sylius practices left after deduplication
   */
  _recordPracticeSystems(newPractices, oldPractices, keptOldPractices) {
    [...keptOldPractices, ...newPractices].forEach((practice) => {
      this._practiceSystemMap.set(practice.id, practice._system);
    });

    const oldPracticesByName = new Map(
      oldPractices.map((practice) => [practice.longName.toLowerCase(), practice]),
    );

    newPractices.forEach((practice) => {
      const legacyPractice = oldPracticesByName.get(practice.longName.toLowerCase());

      if (legacyPractice) {
        this._practiceLegacyIdMap.set(practice.id, legacyPractice.id);
      }

      this._practiceCutoverMap.set(practice.id, practice.newBookingFlowFrom || null);
    });
  }

  getCustomerInfo(shopifyId) {
    return this._request(`/customer/${shopifyId}/info`, {
      method: 'GET',
    });
  }

  getCustomerPrescriptions(shopifyId) {
    return this._request(`/customer/${shopifyId}/prescriptions`, {
      method: 'GET',
    });
  }

  async eyeExamLocationsAll() {
    // Fetch from both old (Sylius) and new (Practice Management) systems
    const [oldSystemResponse, newSystemResponse] = await Promise.allSettled([
      this._request(`/eye-exam/locations`, {
        needsAuth: true,
        method: 'GET',
      }),
      this._requestNewSystem(`/eye-exam/locations`, {
        needsAuth: false,
        method: 'GET',
      }),
    ]);

    // Extract successful responses
    const oldPractices = oldSystemResponse.status === 'fulfilled' ? oldSystemResponse.value.channels || [] : [];
    const newPractices = newSystemResponse.status === 'fulfilled' ? newSystemResponse.value.channels || [] : [];

    // Mark practices with their system type and normalize format
    const normalizedOldPractices = oldPractices.map((practice) => ({
      ...practice,
      _system: 'old',
      // Normalize offeredFrontBookingTypes to string (old system uses array)
      offeredFrontBookingTypes: Array.isArray(practice.offeredFrontBookingTypes)
        ? practice.offeredFrontBookingTypes.join(',')
        : practice.offeredFrontBookingTypes,
    }));

    const normalizedNewPractices = newPractices.map((practice) => ({
      ...practice,
      _system: 'new',
      // New system already uses comma-separated string
    }));

    // Deduplicate: Remove old system practices that exist in new system (by name)
    const newPracticeNames = new Set(normalizedNewPractices.map(p => p.longName.toLowerCase()));
    const deduplicatedOldPractices = normalizedOldPractices.filter(
      practice => !newPracticeNames.has(practice.longName.toLowerCase())
    );

    // Store system mapping for later routing decisions
    this._recordPracticeSystems(
      normalizedNewPractices,
      normalizedOldPractices,
      deduplicatedOldPractices,
    );

    // Merge and return (new system practices first to appear at top of list)
    return {
      channels: [...normalizedNewPractices, ...deduplicatedOldPractices],
    };
  }

  /**
   * @param examTypeId {number}
   */
  async eyeExamLocations(examTypeId) {
    // Fetch from both old (Sylius) and new (Practice Management) systems
    const [oldSystemResponse, newSystemResponse] = await Promise.allSettled([
      this._request(`/eye-exam/locations/${examTypeId}`, {
        needsAuth: true,
        method: 'GET',
      }),
      this._requestNewSystem(`/eye-exam/locations/${examTypeId}`, {
        needsAuth: false,
        method: 'GET',
      }),
    ]);

    // Extract successful responses
    const oldPractices = oldSystemResponse.status === 'fulfilled' ? oldSystemResponse.value.channels || [] : [];
    const newPractices = newSystemResponse.status === 'fulfilled' ? newSystemResponse.value.channels || [] : [];

    // Mark practices with their system type and normalize format
    const normalizedOldPractices = oldPractices.map((practice) => ({
      ...practice,
      _system: 'old',
      offeredFrontBookingTypes: Array.isArray(practice.offeredFrontBookingTypes)
        ? practice.offeredFrontBookingTypes.join(',')
        : practice.offeredFrontBookingTypes,
    }));

    const normalizedNewPractices = newPractices.map((practice) => ({
      ...practice,
      _system: 'new',
    }));

    // Deduplicate: Remove old system practices that exist in new system (by name)
    const newPracticeNames = new Set(normalizedNewPractices.map(p => p.longName.toLowerCase()));
    const deduplicatedOldPractices = normalizedOldPractices.filter(
      practice => !newPracticeNames.has(practice.longName.toLowerCase())
    );

    // Store system mapping
    this._recordPracticeSystems(
      normalizedNewPractices,
      normalizedOldPractices,
      deduplicatedOldPractices,
    );

    // Merge and return (new system practices first)
    return {
      channels: [...normalizedNewPractices, ...deduplicatedOldPractices],
    };
  }

  /**
   * Available days for a practice, tagged with the system that serves each one.
   *
   * A practice that has been cut over to PMS is served by both systems at once, so the
   * returned days can mix the two. Callers must pass the chosen day back to eyeExamTimes
   * and eyeExamBooking, otherwise those calls can hit the wrong backend.
   *
   * @param locationId {number}
   * @param examTypeId {number}
   * @returns {Promise<{ id: string|number, date: string, _system: 'old'|'new' }[]>}
   */
  async eyeExamLocationDays(locationId, examTypeId) {
    const system = this._practiceSystemMap.get(locationId);

    if (system !== 'new') {
      const days = (await this._request(`/eye-exam/days/${locationId}/${examTypeId}`, {
        needsAuth: true,
        method: 'GET',
      })) || [];

      return this._recordDaySystems(mergeDaysWithLegacyFallback([], days));
    }

    // Cut-over practice: PMS covers the days from its first open day onwards, Sylius still
    // covers everything before that. Only ask Sylius when this practice came from there,
    // otherwise the PMS id could collide with an unrelated Sylius channel id.
    const legacyChannelId = this._practiceLegacyIdMap.get(locationId);

    const [newSystemResponse, oldSystemResponse] = await Promise.allSettled([
      this._requestNewSystem(`/eye-exam/days/${locationId}/${examTypeId}`, {
        needsAuth: true,
        method: 'GET',
      }),
      legacyChannelId === undefined
        ? Promise.resolve([])
        : this._request(`/eye-exam/days/${legacyChannelId}/${examTypeId}`, {
            needsAuth: true,
            method: 'GET',
          }),
    ]);

    if (newSystemResponse.status === 'rejected') {
      // Without PMS there is no cutover date, so every Sylius day would look bookable,
      // including days PMS now owns. Fail instead of offering slots that may be gone.
      console.error('[CubittsAPI] PMS days request failed', newSystemResponse.reason);

      throw newSystemResponse.reason;
    }

    if (oldSystemResponse.status === 'rejected') {
      console.error('[CubittsAPI] Sylius days request failed', oldSystemResponse.reason);
    }

    const newDays = newSystemResponse.value || [];
    const oldDays = oldSystemResponse.status === 'fulfilled' ? oldSystemResponse.value || [] : [];

    return this._recordDaySystems(
      mergeDaysWithLegacyFallback(newDays, oldDays, this._practiceCutoverMap.get(locationId) || null),
    );
  }

  /**
   * Remember which system each day came from so the follow-up time and booking calls
   * go to the same place.
   *
   * @param {{ id: string|number, _system: 'old'|'new' }[]} days
   * @returns {typeof days} the same days, for chaining
   */
  _recordDaySystems(days) {
    days.forEach((day) => {
      this._daySystemMap.set(String(day.id), day._system);
    });

    return days;
  }

  /**
   * @param dayId {string|number}
   * @returns {'old'|'new'|null} null when the day was not returned by eyeExamLocationDays
   */
  getDaySystem(dayId) {
    return this._daySystemMap.get(String(dayId)) || null;
  }

  /**
   * @param dayId {string|number}
   * @param examTypeId {number}
   * @param locationId {number} - Optional, fallback routing when the day is unknown
   */
  async eyeExamTimes(dayId, examTypeId, locationId = null) {
    // Route on the selected day first: a cut-over practice serves days from both systems
    const system = resolveRequestSystem(
      this.getDaySystem(dayId),
      locationId ? this._practiceSystemMap.get(locationId) : null,
    );
    const requestFn = system === 'new' ? this._requestNewSystem.bind(this) : this._request.bind(this);

    return requestFn(`/eye-exam/time/${dayId}/${examTypeId}`, {
      needsAuth: true,
      method: 'GET',
    });
  }

  /**
   * @param body {object}
   * @param locationId {number} - Optional, fallback routing when no dayId is given
   * @param dayId {string|number} - Optional, the day the slot belongs to
   */
  eyeExamBooking(body = {}, locationId = null, dayId = null) {
    // Route on the selected day first, same reason as eyeExamTimes
    const system = resolveRequestSystem(
      dayId !== null ? this.getDaySystem(dayId) : null,
      locationId ? this._practiceSystemMap.get(locationId) : null,
    );
    const requestFn = system === 'new' ? this._requestNewSystem.bind(this) : this._request.bind(this);

    return requestFn(`/eye-exam/book`, {
      body,
      method: 'POST',
      needsAuth: true,
    });
  }

  postPurchaseInfo(orderId) {
    return this._request(`/purchase/${orderId}/info`, {
      method: 'GET',
    });
  }

  postPurchaseSendExisting(body = {}) {
    return this._request(`/purchase/prescription`, { body });
  }

  postPurchaseSendUpload(body = {}) {
    return this._request(`/purchase/prescription`, { body });
  }

  postPurchaseSendLater(body = {}) {
    return this._request(`/purchase/prescription`, { body });
  }

  postPurchasePlano(body = {}) {
    return this._request(`/purchase/plano`, { body });
  }

  /**
   * @param {string} orderId
   * @returns {Promise<{ hash?: string, error?: string }>}
   */
  postPurchaseHash(orderId) {
    return this._request(`/purchase/${orderId}/pd-hash`, {
      needsAuth: true,
      method: 'GET',
    });
  }

  /*
    __cartToken:
    prescriptionId:
  */
  prePurchasePrescriptionExisting(body = {}) {
    return this._request(`/purchase/prescription/SOME_ENDPOINT`, {
      body,
      method: 'POST',
      needsAuth: true,
    });
  }

  /**
   * file in base64
   * __cartToken
   * distanceField:
   */
  /**
   *
   * @param {{ file: string, __cartToken: string, distanceField: string }} body
   * @returns
   */
  prePurchasePrescriptionImageUpload(body) {
    return this._request(`/purchase/prescription/image-upload`, {
      body,
      method: 'POST',
      needsAuth: true,
    });
  }

  /**
   * @param {string} hash
   */
  postPurchasePd(hash, body = {}) {
    return this._request(`/pd-tool/${hash}`, { body, needsAuth: true });
  }

  /**
   * @param {string} hash
   */
  postPurchasePdInfo(hash) {
    return this._request(`/pd-tool/${hash}`, {
      needsAuth: true,
      method: 'GET',
    });
  }

  /**
   * Upload PD measurement image to app-proxy (new prescription system)
   * @param {string} prescriptionId - Prescription ID
   * @param {Blob} imageBlob - Image blob from canvas
   * @param {string} pdValue - Calculated PD value
   * @param {object} metadata - Additional metadata (eye positions, card position, etc.)
   * @returns {Promise}
   */
  async uploadPdImage(prescriptionId, imageBlob, pdValue, metadata = {}) {
    const appProxyUrl = window.theme.isDevTheme
      ? 'https://staging.app-proxy.cubitts.com'
      : 'https://app-proxy.cubitts.com';

    // Convert blob to base64 data URL
    const base64Image = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(imageBlob);
    });

    // Get session token from customer account
    const sessionToken = await this._getCustomerSessionToken();

    const response = await fetch(
      `${appProxyUrl}/api/prescriptions/pd-upload?prescriptionId=${prescriptionId}`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pdImage: base64Image,
          pdValue: pdValue,
          pdImageMetadata: {
            ...metadata,
            capturedAt: new Date().toISOString(),
            source: 'pd-tool-standalone'
          }
        })
      }
    );

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get customer session token for app-proxy authentication
   * @returns {Promise<string>}
   * @private
   */
  async _getCustomerSessionToken() {
    // Session token is available in Shopify customer account context
    // This is provided by Shopify's customer account API
    if (window.Shopify?.customerPrivacy?.userCanBeTracked()) {
      const response = await fetch('/account/session-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      return data.token;
    }
    throw new Error('Customer not authenticated');
  }

  /**
   * @param {string} url
   */
  _request(
    url,
    {
      body = {},
      needsAuth = false,
      headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      ...rest
    } = {},
  ) {
    if (needsAuth) {
      headers['X-Secure-Access'] = this._accessKey;
    }

    body = typeof body === 'string' || body instanceof String ? body : JSON.stringify(body);

    const payload = {
      headers,
      body,
      ...rest,
    };

    if (rest?.method === 'GET') {
      delete payload.body;
    }

    return fetchJSON(`${this._baseUrl}${url}`, payload);
  }

  /**
   * Make request to new practice management system (PMS)
   * Note: PMS endpoints don't use X-Secure-Access header
   * @param {string} url
   */
  _requestNewSystem(
    url,
    {
      body = {},
      headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      ...rest
    } = {},
  ) {
    body = typeof body === 'string' || body instanceof String ? body : JSON.stringify(body);

    const payload = {
      headers,
      body,
      ...rest,
    };

    if (rest?.method === 'GET') {
      delete payload.body;
    }

    return fetchJSON(`${this._newSystemBaseUrl}${url}`, payload);
  }
})();
